import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import {DataModificationsHeader} from 'audit/DataModifications';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import {
  Batch,
  Container,
  ContainerContent,
  ParentStorableData,
  ResponseContainerTransfer,
  StorableSummary,
} from 'constants/SampleConstants';
import {SampleControlParams} from 'constants/SampleStatusConstants';
import {BioentitySummary, TagsDict} from 'folder-items/FileConstants';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';
import {Optional} from 'util/Types';

export function updateTags(
  containerId: string,
  tags: TagUpdateObject,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<Container> {
  return net.patchJSON({
    url: `/1/api/containers/${containerId}/tags`,
    data: {
      tags,
    },
    extraHeaders: dataModsHeader,
  });
}

export interface UpdateContainerParams extends SampleControlParams {
  name?: string;
  barcode?: string;
  parentStorableData?: ParentStorableData;
  sourceId?: string;
  quantity?: LegacyMeasurement | null;
  expirationDateOverride?: Optional<string>;
  freezeThawCount?: Optional<number>;
}

export interface UpdateContainerResponse {
  container: Container;
  parentStorable: StorableSummary | null;
}

export function updateContainer(
  containerId: string,
  data: UpdateContainerParams,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<UpdateContainerResponse> {
  return net.patchJSON({
    url: `/1/api/containers/${containerId}`,
    data,
    extraHeaders: dataModsHeader,
  });
}

export interface UpdateContainerContentResponse {
  containerContent: ContainerContent;
  gridId: string | null;
}

export function updateContainerContent(
  containerId: string,
  containedItemId: string,
  concentration: LegacyMeasurement,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<UpdateContainerContentResponse> {
  return net.patchJSON({
    url: `/1/api/containers/${containerId}:update-container-content`,
    data: {
      containedItemId,
      concentration,
    },
    extraHeaders: dataModsHeader,
  });
}

export interface DeleteContainerContentsResponse {
  invalidatableContainedItemIds: Array<string>;
  invalidatableStorableIds: Array<string>;
  // Deprecated - use invalidatableContainedItemIds instead
  invalidatableBatchIds: Array<string>;
}

export function deleteContainerContents(
  containerIds: Array<string>,
  dataModsHeader?: DataModificationsHeader
): Promise<DeleteContainerContentsResponse> {
  return net.postJSONPromise({
    url: '/1/api/containers:delete-contents',
    data: {containerIds},
    extraHeaders: dataModsHeader,
  });
}

/** Removes one content from a container, leaving its other contents and quantity. */
export function removeContainerContent(
  containerId: string,
  containedItemId: string,
  dataModsHeader?: DataModificationsHeader
): Promise<DeleteContainerContentsResponse> {
  return net.postJSONPromise({
    url: `/1/api/containers/${containerId}:remove-content`,
    data: {containedItemId},
    extraHeaders: dataModsHeader,
  });
}

export interface CreateContainerParam extends SampleControlParams {
  barcode: string | null;
  containerSchemaId: string;
  inputContainableParams: {
    concentration: LegacyMeasurement;
    id: string | null;
    quantity: LegacyMeasurement;
  } | null;
  name: string;
  parentStorableData: ParentStorableData;
  sourceId: string | null;
  tags: TagsDict;
  studyIds?: Array<string>;
}

export interface CreateContainersResponse {
  containers: Array<Container>;
}

export function createContainers(
  containerDatas: Array<CreateContainerParam>
): JQueryPromise<CreateContainersResponse> {
  return net.postJSON({
    url: '/1/api/containers:create',
    data: {containers: containerDatas},
  });
}

export function containerEditorURL(containerId: string): string {
  return `/samples/containers/${containerId}`;
}

export interface FetchContainerResponse {
  container: Container;
  batches: Array<Batch>;
  containedBioentities: Array<BioentitySummary>;
  parentStorable: StorableSummary | null;
}

export function fetch(containerId: string): Promise<FetchContainerResponse> {
  return net.getJSONPromise(`/1/api/containers/${containerId}`);
}

export interface BulkFetchContainersResponse {
  containers: Array<Container>;
  batchesById: {[batchId: string]: Batch};
  fileSummariesById: {[bioentityId: string]: BioentitySummary};
}

export function bulkFetch(
  containerIds: Array<string>,
  silentlyFilterOutInaccessibleContainers: boolean = false
): JQueryPromise<BulkFetchContainersResponse> {
  return net.postJSON({
    url: '/1/api/containers:bulk-get',
    data: {containerIds, silentlyFilterOutInaccessibleContainers},
  });
}

export interface FetchTransfersResponse {
  inputTransfers: Array<ResponseContainerTransfer>;
  outputTransfers: Array<ResponseContainerTransfer>;
}

export function fetchTransfers(containerId: string): Promise<FetchTransfersResponse> {
  return net.getJSONPromise(`/1/api/containers/${containerId}/transfers`);
}

export interface ExportToCSVResponse {
  url: string;
}

export function exportToCSV(containerIds: Array<string>): JQueryPromise<ExportToCSVResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/containers:export-to-csv',
    data: {containerIds},
  });
}

export interface ContainerPropertiesUpdate extends SampleControlParams {
  id: string;
  quantity?: LegacyMeasurement;
  expirationDateOverride?: Optional<string>;
  freezeThawCount?: Optional<number>;
}

export interface BulkUpdateContainerPropertiesResponse {
  containers: Array<Container>;
}

export function bulkUpdateContainerProperties(
  // Setting null for value in quantity is only accepted if updating the unit on an already set quantity
  // Setting null for both value and unit in quantity will clear the existing quantity
  containerUpdates: Array<ContainerPropertiesUpdate>,
  analyticsMetadata?: AnalyticsMetadataForContext,
  dataModsHeader?: DataModificationsHeader
): Promise<BulkUpdateContainerPropertiesResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/containers:bulk-update-properties',
    data: {containers: containerUpdates},
    analyticsMetadata,
    extraHeaders: dataModsHeader,
  });
}
export interface BulkUpdateContainerContentConcentrationsResponse {
  containerContents: Array<ContainerContent>;
}

export interface UpdateContainerContentConcentrationsParam {
  containerId: string;
  containedItemId: string;
  concentration: LegacyMeasurement;
}

export function bulkUpdateContainerContentConcentrations(
  // Setting null for value in concentration is only accepted if updating the unit on an already set concentration
  // Setting null for both value and unit in concentration will clear the existing concentration
  containerContents: Array<UpdateContainerContentConcentrationsParam>,
  dataModsHeader?: DataModificationsHeader
): Promise<BulkUpdateContainerContentConcentrationsResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/containers:bulk-update-content-concentrations',
    data: {containerContents},
    extraHeaders: dataModsHeader,
  });
}
